import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Offcanvas } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useAuth } from "../Context/auth";
import MenuLateral from "../Components/MenuPrincipal/MenuLateral";
import PedidosGrid from "../Components/PedidosGrid/PedidosGrid";
import Pagination from "../Components/Pagination/Pagination";
import './Pedidos.css';

function Pedidos() {
    const { currentUser, appUserData } = useAuth();
    const [pedidos, setPedidos] = useState([]);
    const [pedidosFiltrados, setPedidosFiltrados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');
    const [trigger, setTrigger] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (appUserData && appUserData['ler_pedidos'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para visualizar esta tela.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                navigate('/app/home');
            });
        }
    }, [appUserData, navigate]);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // =================================================================================
    // ESTADOS DE FILTRO
    // =================================================================================
    const [showFiltros, setShowFiltros] = useState(false);
    const [listaProdutos, setListaProdutos] = useState([]); // Lista para o Select

    // Filtros Iniciais (Formulário)
    const initialState = {
        status: [], // Array de strings
        numero: '',
        cliente: '',
        data_inicio: '',
        data_fim: '',
        nota_fiscal: '',
        transportadora: '',
        produto_id: ''
    };

    const [filtrosForm, setFiltrosForm] = useState(initialState);
    const [filtrosAplicados, setFiltrosAplicados] = useState(initialState);
    const [pesquisa, setPesquisa] = useState(''); // Barra de busca rápida
    const [textoPesquisa, setTextoPesquisa] = useState(''); // Debounced

    useEffect(() => {
        const timer = setTimeout(() => {
            setTextoPesquisa(pesquisa);
        }, 500);

        return () => clearTimeout(timer);
    }, [pesquisa]);

    // Reseta página ao pesquisar ou filtrar
    useEffect(() => {
        setCurrentPage(1);
    }, [textoPesquisa, filtrosAplicados]);

    // Opções de Status
    const statusOptions = ['Em Aberto', 'Pago', 'Pago Parcial', 'Enviado', 'Concluído', 'Faturado', 'Cancelado'];

    // =================================================================================
    // BUSCA DE DADOS (API)
    // =================================================================================
    const buscarDados = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const API_URL = process.env.REACT_APP_API_URL;

            // 1. Carrega Produtos (para o filtro)
            try {
                const prodResponse = await fetch(`${API_URL}/api/produtos`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (prodResponse.ok) {
                    const prodData = await prodResponse.json();
                    setListaProdutos(prodData.map(p => ({ id: p.id, descricao: p.descricao, codigo: p.codigo })));
                }
            } catch (err) {
                console.error("Erro ao carregar produtos para filtro:", err);
            }

            // 2. Carrega Pedidos
            const url = new URL(`${API_URL}/api/pedidos`);
            if (filtrosAplicados.produto_id) {
                url.searchParams.append('produtoId', filtrosAplicados.produto_id);
            }

            const response = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error('Falha ao carregar os pedidos.');

            const data = await response.json();
            // Ordenar por ID decrescente
            const dataOrdenada = data.sort((a, b) => b.id - a.id);

            setPedidos(dataOrdenada);
            setPedidosFiltrados(dataOrdenada);
        } catch (error) {
            console.error("Erro ao carregar dados:", error);
            setErro('Ocorreu um erro ao carregar os pedidos.');
        } finally {
            setLoading(false);
        }
    }, [currentUser, filtrosAplicados.produto_id]);

    useEffect(() => {
        buscarDados();
    }, [buscarDados, trigger]);

    // =================================================================================
    // LÓGICA DE FILTRAGEM (FRONT-END)
    // =================================================================================
    useEffect(() => {
        let resultado = [...pedidos];

        // 1. Pesquisa Rápida
        if (textoPesquisa) {
            const termo = textoPesquisa.toLowerCase();
            resultado = resultado.filter(p =>
                String(p.numero || '').toLowerCase().includes(termo) ||
                (p.cliente_nome || '').toLowerCase().includes(termo)
            );
        }

        // 2. Filtros Avançados
        if (filtrosAplicados.status.length > 0) {
            resultado = resultado.filter(p => filtrosAplicados.status.includes(p.status));
        }

        if (filtrosAplicados.numero) {
            resultado = resultado.filter(p => String(p.numero || '').includes(filtrosAplicados.numero));
        }

        if (filtrosAplicados.cliente) {
            resultado = resultado.filter(p => (p.cliente_nome || '').toLowerCase().includes(filtrosAplicados.cliente.toLowerCase()));
        }

        if (filtrosAplicados.transportadora) {
            resultado = resultado.filter(p => (p.transportadora_nome || '').toLowerCase().includes(filtrosAplicados.transportadora.toLowerCase()));
        }

        if (filtrosAplicados.nota_fiscal) {
            resultado = resultado.filter(p => (p.nota_fiscal || '').toLowerCase().includes(filtrosAplicados.nota_fiscal.toLowerCase()));
        }

        if (filtrosAplicados.data_inicio) {
            const dInicio = new Date(filtrosAplicados.data_inicio);
            dInicio.setHours(0, 0, 0, 0);
            resultado = resultado.filter(p => {
                const dPedido = new Date(p.data_cadastro);
                return dPedido >= dInicio;
            });
        }
        if (filtrosAplicados.data_fim) {
            const dFim = new Date(filtrosAplicados.data_fim);
            dFim.setHours(23, 59, 59, 999);
            resultado = resultado.filter(p => {
                const dPedido = new Date(p.data_cadastro);
                return dPedido <= dFim;
            });
        }

        setPedidosFiltrados(resultado);

    }, [pedidos, textoPesquisa, filtrosAplicados]);

    // Lógica de Paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentPedidos = pedidosFiltrados.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
    const handleItemsPerPageChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
    };

    // =================================================================================
    // HANDLERS
    // =================================================================================

    const handleFiltroChange = (e) => {
        const { name, value, checked } = e.target;

        if (name === 'status_check') {
            const statusValue = value;
            setFiltrosForm(prev => {
                const novosStatus = checked
                    ? [...prev.status, statusValue]
                    : prev.status.filter(s => s !== statusValue);
                return { ...prev, status: novosStatus };
            });
        } else {
            setFiltrosForm(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleAplicarFiltros = () => {
        setFiltrosAplicados(filtrosForm);
        setShowFiltros(false);
    };

    const handleLimparFiltros = () => {
        setFiltrosForm(initialState);
        setFiltrosAplicados(initialState);
        setPesquisa('');
        setTextoPesquisa('');
    };

    const handleKeyDownFiltros = (e) => {
        if (e.key === 'Enter') handleAplicarFiltros();
    };

    const deletePedido = async (id) => {
        // Validation for Delete
        if (appUserData && appUserData['excluir_pedidos'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Este usuário não possui permissão de exclusão. Por favor, contate o administrador.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        const { isConfirmed } = await Swal.fire({
            title: 'Você tem certeza?',
            text: 'Itens e financeiros associados podem ser afetados. A exclusão será bloqueada se o pedido já tiver sido faturado ou enviado.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: "#2185c6",
            cancelButtonColor: "#DC3545",
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar'
        });

        if (isConfirmed) {
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const response = await fetch(`${API_URL}/api/pedidos/${id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error);
                }

                setTrigger(Date.now());
                Swal.fire('Excluído!', 'O pedido foi removido com sucesso.', 'success');
            } catch (error) {
                console.error("Erro ao excluir pedido:", error);
                Swal.fire('Erro!', error.message, 'error');
            }
        }
    };

    const hasActiveFilters =
        filtrosAplicados.status.length > 0 ||
        filtrosAplicados.numero ||
        filtrosAplicados.cliente ||
        filtrosAplicados.data_inicio ||
        filtrosAplicados.data_fim ||
        filtrosAplicados.nota_fiscal ||
        filtrosAplicados.transportadora ||
        filtrosAplicados.produto_id;

    return (
        <div className="pedidos-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />

            <div className="content-with-sidebar">

                <div className="pedidos-header">
                    <h1 className="pedidos-title">
                        <i className="fas fa-file-invoice-dollar text-primary"></i>
                        Listagem de <span className="highlight">Pedidos</span>
                    </h1>

                </div>

                <main className="pedidos-content">
                    <div className="pedidos-controls">
                        <Link
                            to="/app/novopedido"
                            className="btn-primary-custom me-3" // added margin-end
                            onClick={(e) => {
                                // Validation for Create
                                if (appUserData && appUserData['cadastrar_pedidos'] === false) {
                                    e.preventDefault();
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Acesso Negado',
                                        text: 'Este usuário não possui permissão de cadastro. Por favor, contate o administrador.',
                                        confirmButtonColor: '#2185c6'
                                    });
                                }
                            }}
                        >
                            <i className="fas fa-plus"></i>Novo Pedido
                        </Link>
                        <div className="search-wrapper">
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input-custom"
                                placeholder="Pesquisar por Nº ou Cliente..."
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                                onKeyPress={(e) => { if (e.key === 'Enter') setTextoPesquisa(pesquisa); }}
                            />
                        </div>



                        <button
                            className="btn-icon-custom position-relative"
                            onClick={() => setShowFiltros(true)}
                            title="Filtros Avançados"
                        >
                            <i className="fas fa-filter"></i>
                            {hasActiveFilters && (
                                <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle">
                                    <span className="visually-hidden">Filtros ativos</span>
                                </span>
                            )}
                        </button>
                        <button className="btn-icon-custom ms-2" onClick={() => setTrigger(Date.now())} title="Atualizar Lista">
                            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                        </button>
                    </div>

                    {erro && (
                        <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <div>{erro}</div>
                        </div>
                    )}

                    {loading ? (
                        <div className="loading-container">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <PedidosGrid
                                arrayPedidos={currentPedidos}
                                clickDelete={deletePedido}
                                userPermissions={appUserData}
                            />

                            <Pagination
                                totalItems={pedidosFiltrados.length}
                                itemsPerPage={itemsPerPage}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            />
                        </>
                    )}
                </main>

                {/* --- SIDEBAR DE FILTROS AVANÇADOS --- */}
                <Offcanvas show={showFiltros} onHide={() => setShowFiltros(false)} placement="end" style={{ zIndex: 10500 }}>
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title><i className="fas fa-filter me-2"></i>Filtros Avançados</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <div className="d-grid gap-3" onKeyDown={handleKeyDownFiltros}>

                            <div className="d-grid gap-2 mb-3">
                                <button className="btn btn-primary-custom" onClick={handleAplicarFiltros}>
                                    <i className="fas fa-check me-2"></i>Aplicar Filtros
                                </button>
                                <button className="btn btn-outline-secondary" onClick={handleLimparFiltros}>
                                    <i className="fas fa-eraser me-2"></i>Limpar Filtros
                                </button>
                            </div>
                            <hr className="m-0" />

                            {/* Status (Multi-select via Checkboxes) */}
                            <div className="card bg-light border-0">
                                <div className="card-body">
                                    <h6 className="card-subtitle mb-2 text-muted fw-bold">Status do Pedido</h6>
                                    <div className="d-flex flex-column gap-2">
                                        {statusOptions.map((opt) => (
                                            <div className="form-check" key={opt}>
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="status_check"
                                                    value={opt}
                                                    id={`check-status-${opt}`}
                                                    checked={filtrosForm.status.includes(opt)}
                                                    onChange={handleFiltroChange}
                                                />
                                                <label className="form-check-label ms-2" htmlFor={`check-status-${opt}`}>{opt}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Número */}
                            <div>
                                <label className="form-label small fw-bold">Número do Pedido</label>
                                <input type="text" className="form-control" name="numero" value={filtrosForm.numero} onChange={handleFiltroChange} placeholder="Ex: 1024" />
                            </div>

                            {/* Cliente */}
                            <div>
                                <label className="form-label small fw-bold">Cliente</label>
                                <input type="text" className="form-control" name="cliente" value={filtrosForm.cliente} onChange={handleFiltroChange} placeholder="Nome do cliente..." />
                            </div>

                            {/* Cargo Data (Intervalo) */}
                            <div className="row g-2">
                                <div className="col-6">
                                    <label className="form-label small fw-bold">Data Inicial</label>
                                    <input type="date" className="form-control" name="data_inicio" value={filtrosForm.data_inicio} onChange={handleFiltroChange} />
                                </div>
                                <div className="col-6">
                                    <label className="form-label small fw-bold">Data Final</label>
                                    <input type="date" className="form-control" name="data_fim" value={filtrosForm.data_fim} onChange={handleFiltroChange} />
                                </div>
                            </div>

                            {/* Nota Fiscal */}
                            <div>
                                <label className="form-label small fw-bold">Nota Fiscal</label>
                                <input type="text" className="form-control" name="nota_fiscal" value={filtrosForm.nota_fiscal} onChange={handleFiltroChange} placeholder="Número da NF..." />
                            </div>

                            {/* Transportadora */}
                            <div>
                                <label className="form-label small fw-bold">Transportadora</label>
                                <input type="text" className="form-control" name="transportadora" value={filtrosForm.transportadora} onChange={handleFiltroChange} placeholder="Nome da transportadora..." />
                            </div>

                            {/* Produto (Select) */}
                            <div>
                                <label className="form-label small fw-bold">Produto (Item)</label>
                                <select className="form-select" name="produto_id" value={filtrosForm.produto_id} onChange={handleFiltroChange}>
                                    <option value="">Todos</option>
                                    {listaProdutos.map(prod => (
                                        <option key={prod.id} value={prod.id}>{prod.codigo ? `${prod.codigo} - ` : ''}{prod.descricao}</option>
                                    ))}
                                </select>
                                <div className="form-text x-small fst-italic">Busca pedidos contendo este item.</div>
                            </div>

                        </div>
                    </Offcanvas.Body>
                </Offcanvas>

            </div>
        </div>
    );
}

export default Pedidos;